import React, { useEffect } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import { JiraBottomRightCornerOutlet } from '@atlassian/jira-layout-controller';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { HELP_PANEL_SPOTLIGHT_NAME } from '../../common/constants';
import { useGlobalChangeboarding } from '../../controllers/store/global';
import { useProgressActions } from '../../controllers/store/progress';
import { useOgConsolidationUserState } from '../../controllers/use-og-consolidation-user-state';
import { useTransitionHomeQueryParams } from '../../controllers/use-transition-home-url-params';
import { ChangeboardingProviders } from '../../services/store';
import { TransitionHomeButton } from './transition-home-button';
import { TransitionHomeModal } from './index';

const TransitionHomeRendererInner = () => {
	const {
		isTransitionHomeModalOpen,
		openTransitionHomeModal,
		closeTransitionHomeModal,
		isTransitionHomeLoading,
		isTransitionHomeActivated,
	} = useGlobalChangeboarding();
	const { isTransitioning } = useOgTenantInfo();
	const { changeActiveContent } = useProgressActions();

	const { data } = useOgConsolidationUserState();
	const isOptimalTooling = ff('platform.show-optimal-tooling_brgom');
	const { clearQueryParams, queryParamsValid, activeTaskIndex, activeContent } =
		useTransitionHomeQueryParams();

	useEffect(() => {
		const isTransitionHomeReady =
			!isTransitionHomeLoading && isTransitionHomeActivated && !isTransitionHomeModalOpen;
		const isTransitionHomeVisible = !data?.isTransitionHomeHidden;
		const isModalReady = isTransitionHomeReady && isTransitionHomeVisible;
		const shouldOpenAndActivateModal = isOptimalTooling && isTransitioning && queryParamsValid;

		if (isModalReady && shouldOpenAndActivateModal) {
			openTransitionHomeModal().then(() => {
				if (activeContent) {
					changeActiveContent(activeContent, activeTaskIndex);
					const nodes = document.querySelectorAll('.transition-home-content-box');
					const node = nodes[activeTaskIndex];

					if (node) {
						node.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}

					clearQueryParams();
				}
			});
		}
	}, [
		activeContent,
		activeTaskIndex,
		changeActiveContent,
		clearQueryParams,
		data?.isTransitionHomeHidden,
		isOptimalTooling,
		isTransitionHomeActivated,
		isTransitionHomeLoading,
		isTransitionHomeModalOpen,
		isTransitioning,
		openTransitionHomeModal,
		queryParamsValid,
	]);

	if (!isTransitioning || !isTransitionHomeActivated) {
		return null;
	}

	return (
		<>
			<ContextualAnalyticsData sourceName="transitionHomeModal" sourceType={SCREEN}>
				<TransitionHomeModal
					isOpen={isTransitionHomeModalOpen}
					onClose={closeTransitionHomeModal}
				/>
			</ContextualAnalyticsData>

			{!data?.isTransitionHomeHidden && !isTransitionHomeModalOpen && (
				<JiraBottomRightCornerOutlet orderFromRight={5} id="servicedesk-eoc-transition-home-button">
					<TransitionHomeButton
						isLoading={isTransitionHomeLoading}
						onClick={openTransitionHomeModal}
					/>
				</JiraBottomRightCornerOutlet>
			)}

			<EngagementSpotlight engagementId={HELP_PANEL_SPOTLIGHT_NAME} />
		</>
	);
};

export const TransitionHomeRenderer = () => (
	<JSErrorBoundary
		id="eoc.changeboarding.transition.home"
		packageName="jiraEocChangeboarding"
		fallback="flag"
		teamName="alexstrasza"
	>
		<ChangeboardingProviders>
			<TransitionHomeRendererInner />
		</ChangeboardingProviders>
	</JSErrorBoundary>
);
